import React, { useEffect, type Dispatch } from 'react';
import type { RestaurantFormData } from '../restaurant.types';

interface HoursStepProps {
  formData: RestaurantFormData | null;
  updateFormData: (data: Partial<RestaurantFormData>) => void | null;
  isActive: boolean | null;
}

const HoursStep: React.FC<HoursStepProps> = ({
  formData,
  updateFormData,
  isActive,
}) => {
  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ] as const;

  const updateHours = (day: keyof typeof formData.openingHours, open: string, close: string, isClosed: boolean) => {
    const newHours = {
      ...formData.openingHours,
      [day]: isClosed ? 'Closed' : `${open} - ${close}`
    };
    updateFormData({ openingHours: newHours });
  };
  useEffect(() => {
    // Ensure openingHours is initialized with default values
    if (!formData.openingHours || Object.keys(formData.openingHours).length === 0) {
      const defaultHours = {
        monday: '10:00 - 23:00',
        tuesday: '10:00 - 23:00',
        wednesday: '10:00 - 23:00',
        thursday: '10:00 - 23:00',
        friday: '10:00 - 23:00',
        saturday: '10:00 - 23:00',
        sunday: '10:00 - 23:00',
      };
      updateFormData({ openingHours: defaultHours });
    }
  }, [formData.openingHours, updateFormData]);

  const parseHours = (hours: string | undefined) => {
     if (!hours) return { open: '10:00', close: '23:00', isClosed: false };
    if (hours === 'Closed') return { open: '', close: '', isClosed: true };
    
    const [open, close] = hours.split(' - ');
    return { open: open || '10:00', close: close || '23:00', isClosed: false };
  };

  return (
    <div className="space-y-6">
      {/* Hours Table */}
      <div className={isActive ? 'fade-in stagger-1' : 'opacity-0'}>
        <div className="bg-white rounded-lg border border-[#FFDDA6] overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 bg-[#FFF8E7] px-4 py-3 border-b border-[#FFDDA6]">
            <div className="col-span-3">
              <span className="text-sm font-semibold text-[#111827]">Day</span>
            </div>
            <div className="col-span-6 flex justify-end">
              <span className="text-sm font-semibold text-[#111827]">Opening Hours(24)</span>
            </div>
            <div className="col-span-3 flex justify-end">
              <span className="text-sm font-semibold text-[#111827]">Status</span>
            </div>
          </div>

          {/* Days List */}
        <div className="divide-y divide-[#FFDDA6]">
          {days.map(({ key, label }, index) => {
            const { open, close, isClosed } = parseHours(formData.openingHours[key]);

            return (
              <div
                key={key}
                className={`grid grid-cols-12 gap-2 px-2 py-2 items-center hover:bg-[#FFF8E7] transition-colors ${
                  isActive ? 'fade-in stagger-' + (index + 2) : 'opacity-0'
                }`}
              >
                {/* Day Name */}
                <div className="col-span-3 sm:col-span-3">
                  <span className="text-sm font-medium text-[#111827]">{label}</span>
                </div>

                {/* Time Inputs */}
                <div className="col-span-5 sm:col-span-6 flex flex-wrap items-center gap-2">
                  {isClosed ? (
                    <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium border border-red-200">
                      Closed
                    </span>
                  ) : (
                    <>
                      {/* Open Time */}
                      <div className="relative flex-1 min-w-[80px]">
                        <input
                          type="time"
                          value={open}
                          onChange={(e) => updateHours(key, e.target.value, close, false)}
                          className="w-full pl-8 pr-2 py-1.5 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent text-xs"
                        />
                        <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[#5E503F]">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      </div>

                      <span className="text-[#5E503F] text-xs whitespace-nowrap">to</span>

                      {/* Close Time */}
                      <div className="relative flex-1 min-w-[80px]">
                        <input
                          type="time"
                          value={close}
                          onChange={(e) => updateHours(key, open, e.target.value, false)}
                          className="w-full pl-8 pr-2 py-1.5 border border-[#FFDDA6] rounded-lg focus:ring-2 focus:ring-[#FFD166] focus:border-transparent text-xs"
                        />
                        <div className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[#5E503F]">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Closed Toggle */}
                <div className="col-span-4 flex justify-end items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={isClosed}
                        onChange={(e) => updateHours(key, open, close, e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        className={`w-10 h-5 rounded-full transition-colors ${
                          isClosed ? 'bg-red-500' : 'bg-[#FFDDA6]'
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                            isClosed ? 'translate-x-5' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-[#5E503F]">{isClosed ? 'Closed' : 'Open'}</span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className={isActive ? 'fade-in stagger-9' : 'opacity-0'}>
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            type="button"
            onClick={() => {
              const standardHours = {
                monday: '10:00 - 23:00',
                tuesday: '10:00 - 23:00',
                wednesday: '10:00 - 23:00',
                thursday: '10:00 - 23:00',
                friday: '10:00 - 23:00',
                saturday: '10:00 - 23:00',
                sunday: '10:00 - 23:00'
              };
              updateFormData({ openingHours: standardHours });
            }}
            className="px-4 py-2 text-sm border border-[#FFDDA6] text-[#5E503F] rounded-lg hover:bg-[#FFDDA6] transition-colors"
          >
            Set All Standard Hours
          </button>
          <button
            type="button"
            onClick={() => {
              const closedHours = {
                monday: 'Closed',
                tuesday: 'Closed',
                wednesday: 'Closed',
                thursday: 'Closed',
                friday: 'Closed',
                saturday: 'Closed',
                sunday: 'Closed'
              };
              updateFormData({ openingHours: closedHours });
            }}
            className="px-4 py-2 text-sm border border-[#FFDDA6] text-[#5E503F] rounded-lg hover:bg-[#FFDDA6] transition-colors"
          >
            Close All Days
          </button>
          <button
            type="button"
            onClick={() => {
              const weekendHours = {
                ...formData.openingHours,
                saturday: 'Closed',
                sunday: 'Closed'
              };
              updateFormData({ openingHours: weekendHours });
            }}
            className="px-4 py-2 text-sm border border-[#FFDDA6] text-[#5E503F] rounded-lg hover:bg-[#FFDDA6] transition-colors"
          >
            Close Weekend
          </button>
        </div>
      </div>

    </div>
  );
};

export default HoursStep;